@if (loading) {
  <p class="mod-loading">Loading reports...</p>
}

@if (!loading && openReports.length === 0 && closedReports.length === 0) {
  <p class="mod-empty">No reports found</p>
}

@if (!loading && (openReports.length > 0 || closedReports.length > 0)) {
  <div class="mod-sections">
    <!-- Open / In Review -->
    <section class="mod-section" aria-label="Open reports">
      <h2 class="mod-section-title">Open Reports ({{ openReports.length }})</h2>

      @if (openReports.length === 0) {
        <p class="mod-empty">No open reports</p>
      }

      @for (group of openReports; track group.contentId) {
        <div class="mod-card">
          <div class="mod-card-header">
            <span
              class="mod-badge"
              [class.mod-badge-post]="group.type === 'post'"
              [class.mod-badge-comment]="group.type === 'comment'"
            >
              {{ group.type === 'post' ? 'Post' : 'Comment' }}
            </span>
            <span class="mod-report-count"
              >{{ group.reports.length }}
              {{ group.reports.length === 1 ? 'report' : 'reports' }}</span
            >
          </div>

          <div class="mod-content">
            <p>{{ group.content }}</p>
          </div>

          <button
            class="mod-toggle"
            (click)="toggleExpanded(group)"
            [attr.aria-expanded]="group.expanded"
            [attr.data-testid]="'toggle-reporters-' + group.type + '-' + group.contentId"
            aria-label="Toggle reporter details"
          >
            <span class="mod-toggle-icon" [class.mod-toggle-open]="group.expanded">▶</span>
            View reporters &amp; reasons
          </button>

          @if (group.expanded) {
            <div class="mod-reporters" role="list" aria-label="Reporters">
              @for (report of group.reports; track report.id) {
                <div class="mod-reporter-row" role="listitem">
                  <div class="mod-reporter-name">
                    {{ report.reporter?.first_name || 'Unknown' }}
                    {{ report.reporter?.last_name || '' }}
                  </div>
                  <div class="mod-reporter-reason">{{ formatReason(report.reason) }}</div>
                  @if (report.details) {
                    <div class="mod-reporter-details">"{{ report.details }}"</div>
                  }
                  <div class="mod-reporter-date">{{ formatDate(getReportDate(report)) }}</div>
                </div>
              }
            </div>
          }

          <div class="mod-actions">
            <button
              class="mod-btn mod-btn-review"
              (click)="markReviewed(group)"
              aria-label="Mark as reviewed"
              [attr.data-testid]="'mark-reviewed-' + group.type + '-' + group.contentId"
            >
              Mark Reviewed
            </button>
            <button
              class="mod-btn mod-btn-dismiss"
              (click)="dismiss(group)"
              aria-label="Dismiss report"
              [attr.data-testid]="'dismiss-' + group.type + '-' + group.contentId"
            >
              Dismiss
            </button>
          </div>
        </div>
      }
    </section>

    <!-- Closed / Reviewed -->
    <section class="mod-section" aria-label="Closed reports">
      <h2 class="mod-section-title">Closed Reports ({{ closedReports.length }})</h2>

      <div class="mod-filters">
        <button
          class="mod-filter-btn"
          [class.mod-filter-active]="closedFilter === 'all'"
          (click)="setClosedFilter('all')"
          data-testid="filter-all"
        >
          All
        </button>
        <button
          class="mod-filter-btn"
          [class.mod-filter-active]="closedFilter === 'reviewed'"
          (click)="setClosedFilter('reviewed')"
          data-testid="filter-reviewed"
        >
          Reviewed
        </button>
        <button
          class="mod-filter-btn"
          [class.mod-filter-active]="closedFilter === 'dismissed'"
          (click)="setClosedFilter('dismissed')"
          data-testid="filter-dismissed"
        >
          Dismissed
        </button>
      </div>

      @if (filteredClosedReports.length === 0) {
        <p class="mod-empty">No closed reports</p>
      }

      @for (group of filteredClosedReports; track group.contentId) {
        <div class="mod-card mod-card-closed">
          <div class="mod-card-header">
            <span
              class="mod-badge"
              [class.mod-badge-post]="group.type === 'post'"
              [class.mod-badge-comment]="group.type === 'comment'"
            >
              {{ group.type === 'post' ? 'Post' : 'Comment' }}
            </span>
            <span
              class="mod-status-badge"
              [class.mod-status-reviewed]="group.resolvedStatus === 'reviewed'"
              [class.mod-status-dismissed]="group.resolvedStatus === 'dismissed'"
            >
              {{ group.resolvedStatus === 'reviewed' ? 'Reviewed' : 'Dismissed' }}
            </span>
            <span class="mod-report-count"
              >{{ group.reports.length }}
              {{ group.reports.length === 1 ? 'report' : 'reports' }}</span
            >
          </div>

          <div class="mod-content">
            <p>{{ group.content }}</p>
          </div>

          <button
            class="mod-toggle"
            (click)="toggleExpanded(group)"
            [attr.aria-expanded]="group.expanded"
            [attr.data-testid]="'toggle-reporters-closed-' + group.type + '-' + group.contentId"
            aria-label="Toggle reporter details"
          >
            <span class="mod-toggle-icon" [class.mod-toggle-open]="group.expanded">▶</span>
            View reporters &amp; reasons
          </button>

          @if (group.expanded) {
            <div class="mod-reporters" role="list" aria-label="Reporters">
              @for (report of group.reports; track report.id) {
                <div class="mod-reporter-row" role="listitem">
                  <div class="mod-reporter-name">
                    {{ report.reporter?.first_name || 'Unknown' }}
                    {{ report.reporter?.last_name || '' }}
                  </div>
                  <div class="mod-reporter-reason">{{ formatReason(report.reason) }}</div>
                  @if (report.details) {
                    <div class="mod-reporter-details">"{{ report.details }}"</div>
                  }
                  <div class="mod-reporter-date">{{ formatDate(getReportDate(report)) }}</div>
                </div>
              }
            </div>
          }
        </div>
      }
    </section>
  </div>
}
